import { AxiosResponse } from "axios";
import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { isModelStale, processCoreModelResponse } from "@/store/-utils";
import ActivityAuthApi from "@/store/ltks/-activity-auth-api";
import ActivityPublicApi from "@/store/ltks/-activity-public-api";
import LtksApi from "@/store/ltks/-api";

const emptyFeedFactory: () => LtkFeed = () => ({ ids: [], meta: null });

@Module({
  name: "ltks",
  stateFactory: true,
  namespaced: true,
})
export default class LtksStore extends VuexModule {
  ltks: LtkMap = {};
  feeds: LtkFeeds = {};
  postFeeds: PostFeeds = {};

  get getFeedHasMorePages() {
    return (feedName: string) => {
      const feed = this.ltkFeed(feedName);
      return (feed.ids.length === 0 && feed.meta === null) || Boolean(feed.meta?.nextUrl);
    };
  }

  get getFeedV3HasMorePages() {
    return (feedName: string) => {
      const feed = this.ltkFeed(feedName) as PostFeed;
      return (feed.ids.length === 0 && feed.meta === null) || Boolean(feed.meta?.next);
    };
  }

  /**
   * Regardless if a feed has been fetched, returns if the given feed has a non-zero number of posts
   */
  get getFeedHasNoLtks() {
    return (feedName: string) => {
      const feed = this.ltkFeed(feedName);
      return feed.ids.length === 0;
    };
  }

  get hasFeedBeenFetched() {
    return (feedName: string) => {
      const feed = this.ltkFeed(feedName);
      return feed && feed.meta !== null && feed.ids.length > 0;
    };
  }

  get ltkImageAspectRatio() {
    return (id: string) => {
      const ltk = this.ltks[id];
      return (ltk?.heroImageWidth || 1) / (ltk?.heroImageHeight || 1);
    };
  }

  get ltkFeed() {
    return (feedName: string) =>
      this.feeds[feedName.toLowerCase()] ?? this.postFeeds[feedName.toLowerCase()] ?? emptyFeedFactory();
  }

  get getTotalNumberOfLtks() {
    return (feedName: string) => this.ltkFeed(feedName).meta?.totalResults ?? 0;
  }

  /**
   * Central method to fetch and set feeds of ltk posts.
   * This action should not be called directly from the application, and instead should be proxied
   * through other preset actions to form the correct api data payload and feed name. Pagination
   * will be applied to the callback function automatically based on the current state of the feed.
   *
   * @param {string} feedOptions.feedName
   *        Label reference to the feed to be feetched/set.
   * @param {(params: PostMeta | null) => Promise<AxiosResponse<ManyActivitiesResponse, unknown>>} feedOptions.callback
   *        A servcice/API callback function. This should accept pagination params, and return many acttivities.
   * @param {boolean} feedOptions.overwrite
   *        Optional flag to overwrite the current feed, fetching as if this were the first time.
   */
  @Action({ rawError: true })
  async fetchPostFeedV3({
    feedName,
    overwrite = false,
    callback,
  }: {
    feedName: string;
    overwrite?: boolean;
    callback: (params: PostMeta | null) => Promise<AxiosResponse<ManyActivitiesResponse, unknown>>;
  }) {
    let data: PostMeta | null = null;

    const meta = this.postFeeds[feedName.toLowerCase()]?.meta || null;
    if (!this.getFeedV3HasMorePages(feedName) && overwrite === false) {
      return;
    }

    if (overwrite === false && meta?.next !== undefined) {
      data = meta;
    }

    const response = await callback(data);

    const ltkIds = response.data.activities?.map((item) => item.postId);

    // Set core models before feed lists
    await processCoreModelResponse(
      this.store,
      {
        profiles: response.data.profiles || [],
        ltks: response.data.ltks || [],
        mediaObjects: response.data.mediaObjects || [],
      },
      true,
    );
    this.setPostFeed({ feedName, ids: ltkIds, meta: response.data.meta, overwrite });
  }

  /**
   * Post Feed Preset: following
   * Fetches a feed of posts by creators the current user is following
   *
   * @param {boolean} feedOptions.overwrite
   *        Optional flag to overwrite the current feed, fetching as if this were the first time.
   */
  @Action({ rawError: true })
  async fetchFollowingLtks({ overwrite = false }: { overwrite?: boolean }) {
    const feedName = "following";
    const api = new ActivityAuthApi(this.store.$axios);
    await this.fetchPostFeedV3({ feedName, overwrite, callback: api.getFollowingPosts.bind(api) });
  }

  /**
   * Post Feed Preset: :profileId
   * Fetches a feed of posts by one creator.
   *
   * @param {string} feedOptions.profileId
   *        The profile ID of the creator shop
   * @param {boolean} feedOptions.overwrite
   *        Optional flag to overwrite the current feed, fetching as if this were the first time.
   */
  @Action({ rawError: true })
  async fetchLtksByProfileId({
    profileId,
    overwrite,
    params = null,
  }: {
    profileId: string;
    overwrite?: boolean;
    params?: ProfilePostsActivityMeta | null;
  }) {
    const api = new ActivityPublicApi(this.store.$axios);
    await this.fetchPostFeedV3({
      feedName: profileId,
      overwrite,
      callback: api.getPostsByProfileId.bind(api, profileId, params),
    });
  }

  /**
   * Post Feed Preset: :hashtagName
   * In general, it fetches a feed of posts tagged by the provided category or hashtag name. There
   * may be certain items represented as a category that use a separate serice callback to perform
   * specific business logic on the backend for filtering of posts from the feed, for example within
   * a time range, or ranked by popularity. While the business logic is abstracted away, this
   * function still needss to determine the correct service endgoint to use.
   *
   * @param {string} feedOptions.hashtagName
   *        The name of the category/hashtag to be fetched.
   * @param {boolean} feedOptions.overwrite
   *        Optional flag to overwrite the current feed, fetching as if this were the first time.
   */
  @Action({ rawError: true })
  async fetchLtksByHashtag({ hashtagName, overwrite }: { hashtagName: string; overwrite?: boolean }) {
    // Use a # prefix to namespace hashtags away from profiles
    const feedName = `#${hashtagName}`;
    const api = new ActivityPublicApi(this.store.$axios);

    switch (hashtagName.toLowerCase()) {
      case "ltkpopulartoday":
        await this.fetchPostFeedV3({ feedName, overwrite, callback: api.getTrendingTodayPosts.bind(api) });
        break;
      case "ltktrendingcreators":
        await this.fetchPostFeedV3({ feedName, overwrite, callback: api.getTrendingCreatorPosts.bind(api) });
        break;
      case "ltktopshared":
        await this.fetchPostFeedV3({ feedName, overwrite, callback: api.getTopSharedPosts.bind(api) });
        break;
      default:
        await this.fetchPostFeedV3({
          feedName,
          overwrite,
          callback: api.getHashtagPosts.bind(api, hashtagName),
        });
    }
  }

  /**
   * Post Feed Preset: discover
   * Fetches a feed of posts from the dynamic "discover" activity feed.
   *
   * @param {string} feedOptions.profileId
   *        The profile ID of the creator shop
   * @param {boolean} feedOptions.overwrite
   *        Optional flag to overwrite the current feed, fetching as if this were the first time.
   */
  @Action({ rawError: true })
  async fetchLtksByFeatured({ overwrite }: { overwrite?: boolean }) {
    const feedName = "discover";
    const api = new ActivityPublicApi(this.store.$axios);
    await this.fetchPostFeedV3({ feedName, overwrite, callback: api.getFeaturedPosts.bind(api) });
  }

  /**
   * Normally calling 1 mutation for every 1 model would be discouraged, however, the underlying
   * API call has a special debounce and argument aggregation decorator on it. Because of this,
   * multiple rapid calls to the API will be aggregated into a single call, resulting with only
   * the last call returning results, and the earlier ones having a rejected/cancelled promise.
   */
  @Action({ rawError: true })
  async fetchLtkByIdAggregate({ id, overwrite }: { id: string; overwrite?: boolean }) {
    // Skip refreshing recent models, force refetch when overwrite is true
    if (!isModelStale(this.ltks[id]) && !overwrite) {
      return;
    }

    try {
      const api = new LtksApi(this.store.$axios);
      const response = await api.getOneAggregate(id);

      await processCoreModelResponse(this.store, response.data);
    } catch (error) {
      // null is returned from the debounce decorator. All other errors should be considered action failures.
      if (error === null) {
        return;
      }

      throw error;
    }
  }

  @Action({ rawError: true })
  async fetchLtkById({ id, fetchNestedModels }: { id: string; fetchNestedModels?: boolean }) {
    // Skip refreshing recent models
    if (!isModelStale(this.ltks[id])) {
      return;
    }

    try {
      const api = new LtksApi(this.store.$axios);
      const response = await api.getOne(id);

      await processCoreModelResponse(this.store, response.data, fetchNestedModels);
    } catch (error) {
      // null is returned from the debounce decorator. All other errors should be considered action failures.
      if (!error === null) {
        throw error;
      }
    }
  }

  @Action({ rawError: true })
  async fetchLtksByIds(ids: string[]) {
    // Skip refreshing recent models
    const staleIds = ids.filter((id) => isModelStale(this.ltks[id]));
    if (staleIds.length === 0) {
      return;
    }

    try {
      const api = new LtksApi(this.store.$axios);
      const response = await api.get({ ids });

      await processCoreModelResponse(this.store, response.data);
    } catch (error) {
      // null is returned from the debounce decorator. All other errors should be considered action failures.
      if (!error === null) {
        throw error;
      }
    }
  }

  @Mutation
  setLtkFeed({
    feedName,
    ids,
    meta,
    overwrite = false,
  }: {
    feedName: string;
    ids: string[];
    meta?: LtkMeta | null;
    overwrite: boolean;
  }) {
    // Normalize feed name
    const feed = feedName.toLowerCase();

    // Create feed if it doesn't exist
    if (this.feeds[feed] === undefined) {
      Vue.set(this.feeds, feed, emptyFeedFactory());
    }

    // Add items to feed
    const start = overwrite ? 0 : this.feeds[feed].ids.length;
    const length = overwrite ? this.feeds[feed].ids.length : 0;
    this.feeds[feed].ids.splice(start, length, ...ids);

    Vue.set(this.feeds[feed], "meta", meta ?? null);
  }

  @Mutation
  setPostFeed({
    feedName,
    ids,
    meta,
    overwrite = false,
  }: {
    feedName: string;
    ids: string[];
    meta?: PostMeta | null;
    overwrite: boolean;
  }) {
    // Normalize feed name
    const feed = feedName.toLowerCase();

    // Create feed if it doesn't exist
    if (this.postFeeds[feed] === undefined) {
      Vue.set(this.postFeeds, feed, emptyFeedFactory());
    }

    // Add items to feed
    const start = overwrite ? 0 : this.postFeeds[feed].ids.length;
    const length = overwrite ? this.postFeeds[feed].ids.length : 0;
    this.postFeeds[feed].ids.splice(start, length, ...ids);

    Vue.set(this.postFeeds[feed], "meta", meta ?? null);
  }

  @Mutation
  setLtks({ ltks }: { ltks: LtkMap }) {
    // Clone and merge resulting ltks with existing store values
    ltks = Object.values(ltks as Record<string, Ltk>).reduce((acc, ltk) => {
      acc[ltk.id] = Object.assign({}, this.ltks[ltk.id], ltks[ltk.id]);
      return acc;
    }, ltks);

    Vue.set(this, "ltks", Object.assign({}, this.ltks, ltks));
  }
}

export const getLtksStore = (store: Store<unknown>) => {
  const ltksStore = getModule(LtksStore, store);

  return ltksStore;
};

export const ltksStorePlugin = (store: Store<unknown>) => {
  store.subscribe(({ type, payload }) => {
    switch (type) {
      case "follower/addFollow":
        getLtksStore(store).setLtkFeed({ feedName: "following", ids: [], meta: null, overwrite: true });
        break;
      case "follower/removeFollow": {
        const ltksStore = getLtksStore(store);
        const updatedList = ltksStore
          .ltkFeed("following")
          .ids.filter((ltkId) => ltksStore?.ltks[ltkId]?.profileId !== payload);
        const minimumFollowFeed = 4;
        if (updatedList.length < minimumFollowFeed) {
          ltksStore.setLtkFeed({ feedName: "following", ids: [], meta: null, overwrite: true });
        } else {
          ltksStore.setLtkFeed({ feedName: "following", ids: updatedList, meta: null, overwrite: true });
        }
        break;
      }
    }
  });
};
